import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adsApiCall } from "./api";
import { Search, Loader2, ArrowLeft, Film } from "lucide-react";
import AchievementCelebration from "./AchievementCelebration";
import TemplatePreviewModal from "./TemplatePreviewModal";
import { StudioShell } from "../studio/StudioShell";
import { useLanguage } from "../contexts/LanguageContext";
import "./ads-creator.css";

const CATEGORY_LABEL = {
  ecommerce: "E-commerce", restaurants: "Restaurants", real_estate: "Real Estate",
  fitness: "Fitness", beauty: "Beauty", tech: "Tech", b2b_saas: "B2B SaaS",
  events: "Events", automotive: "Automotive", travel: "Travel",
  education: "Education", healthcare: "Healthcare", finance: "Finance",
  fashion: "Fashion", food: "Food", pets: "Pets", sports: "Sports",
  music: "Music", art: "Art", photography: "Photography",
};

const FORMAT_LABEL = {
  facebook_feed: "FB Feed", facebook_header: "FB Header", facebook_profile: "FB Profile",
  facebook_story: "FB Story", instagram_feed: "IG Feed", instagram_story: "IG Story",
  instagram_profile: "IG Profile", instagram_reel: "IG Reel",
  google_display: "Google", linkedin_banner: "LinkedIn",
};

export default function TemplateGallery() {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [formats, setFormats] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [activeFmt, setActiveFmt] = useState("all");
  const [activePack, setActivePack] = useState("all"); // all | trending | premium_v1 | premium_v2_ai | video | free
  const [videoOnly, setVideoOnly] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 60;
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [previewTpl, setPreviewTpl] = useState(null);
  const navigate = useNavigate();
  // ?type=post|story|reel|video|ad — pre-set the format filter accordingly
  const typeParam = new URLSearchParams(window.location.search).get("type");

  useEffect(() => {
    // Map studio content-type → format filter (one-shot on mount)
    const TYPE_FORMAT = {
      post: "instagram_feed",
      story: "instagram_story",
      reel: "instagram_reel",
      video: "facebook_feed",
      ad: "facebook_feed",
    };
    if (typeParam && TYPE_FORMAT[typeParam]) {
      setActiveFmt(TYPE_FORMAT[typeParam]);
      if (typeParam === "reel") setActivePack("video");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    adsApiCall.meta().then((m) => {
      setCategories(m.categories);
      setFormats(m.formats);
    });
  }, []);

  // Reset pagination whenever a filter changes
  useEffect(() => { setPage(0); }, [activeCat, activeFmt, videoOnly, q, activePack]);

  // Fetch templates whenever filters or page change
  useEffect(() => {
    setLoading(true);
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    if (activeCat !== "all") params.category = activeCat;
    if (activeFmt !== "all") params.format = activeFmt;
    if (videoOnly || activePack === "video") params.animated = true;
    if (activePack === "premium_v1" || activePack === "premium_v2_ai") {
      params.pack = activePack;
    } else if (activePack === "free") {
      params.premium = false;
    } else if (activePack === "trending") {
      // Trending = top by usage_count, server already sorts by usage_count DESC
      params.limit = 60;
      params.offset = 0;
    }
    if (q.trim()) params.q = q.trim();
    adsApiCall.listTemplates(params)
      .then((r) => { setTemplates(r.templates); setTotal(r.total); })
      .finally(() => setLoading(false));
  }, [activeCat, activeFmt, videoOnly, q, page, activePack]);

  // Debounce search: commit after 250ms idle
  const [qDraft, setQDraft] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ(qDraft), 250);
    return () => clearTimeout(t);
  }, [qDraft]);

  async function startFromTemplate(t) {
    setStarting(t.template_id);
    try {
      const proj = await adsApiCall.useTemplate(t.template_id);
      navigate(`/ads-creator/editor/${proj.project_id}`);
    } finally {
      setStarting(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <StudioShell activeKey="/ads-creator/templates">
    <div className="ads-root" data-testid="ads-gallery">
      <div className="ads-hero" style={{ padding: "48px 28px 28px" }}>
        <div className="ads-container">
          {typeParam && (
            <div data-testid="gallery-type-pill" style={{
              display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14,
              padding: "6px 14px", borderRadius: 999,
              background: "rgba(168,85,247,0.12)", border: "1px solid rgba(168,85,247,0.35)",
              color: "#c084fc", fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase",
            }}>
              {t.studio_gallery_filter_prefix || "Filter"}: {{post:(t.studio_gallery_type_post||"Post"),story:(t.studio_gallery_type_story||"Story"),reel:(t.studio_gallery_type_reel||"Reel"),video:(t.studio_gallery_type_video||"Video"),ad:(t.studio_gallery_type_ad||"Ad")}[typeParam] || typeParam}
              <Link to="/ads-creator/templates" style={{ color: "#c084fc", textDecoration: "underline" }}>{t.studio_gallery_filter_cancel || "× clear"}</Link>
            </div>
          )}
          <h1 className="ads-h1" style={{ fontSize: "clamp(32px, 4.5vw, 54px)" }}>{t.studio_gallery_title || "Templates"}</h1>
          <p className="ads-lead">{t.studio_gallery_lead || "Pick a start. Edit anything."}</p>
        </div>
      </div>

      <div className="ads-section" style={{ paddingTop: 20 }}>
        <div className="ads-container">
          <div className="ads-gallery-head">
            <div style={{ position: "relative", minWidth: 260 }}>
              <Search size={16} style={{ position: "absolute", left: 12, top: 11, color: "var(--ads-text-dim)" }} />
              <input
                placeholder={t.studio_gallery_search || "Search templates…"}
                value={qDraft}
                onChange={(e) => setQDraft(e.target.value)}
                className="ads-panel-input"
                style={{ paddingLeft: 36, margin: 0 }}
                data-testid="ads-gallery-search"
              />
            </div>
            <div style={{ color: "var(--ads-text-dim)", fontSize: 13 }} data-testid="ads-gallery-count">
              {loading ? (t.studio_gallery_loading_count || "Loading…") : (t.studio_gallery_count_of || "{start}–{end} of {total} templates").replace("{start}", templates.length ? (page * 60 + 1) : 0).replace("{end}", page * 60 + templates.length).replace("{total}", total)}
            </div>
          </div>

          <div className="ads-filter-row" style={{ marginBottom: 14 }} data-testid="ads-filter-packs">
            {[
              { id: "all",            label: (t.studio_gallery_pack_all || "All"),          emoji: "◉" },
              { id: "trending",       label: (t.studio_gallery_pack_trending || "Trending"),     emoji: "🔥" },
              { id: "premium_v2_ai",  label: (t.studio_gallery_pack_ai || "AI-Powered"),   emoji: "✨" },
              { id: "premium_v1",     label: (t.studio_gallery_pack_editorial || "Editorial"),    emoji: "📐" },
              { id: "video",          label: (t.studio_gallery_pack_video || "Video Reels"),  emoji: "🎬" },
              { id: "free",           label: (t.studio_gallery_pack_free || "Free"),         emoji: "○" },
            ].map((p) => {
              const active = activePack === p.id;
              return (
                <button
                  key={p.id}
                  className={`ads-chip ${active ? "active" : ""}`}
                  onClick={() => setActivePack(p.id)}
                  data-testid={`ads-filter-pack-${p.id}`}
                  style={active ? {
                    background: "linear-gradient(90deg,#a855f7,#7c3aed)",
                    borderColor: "transparent", color: "#fff", fontWeight: 700,
                  } : undefined}
                >
                  <span style={{ fontSize: 12, marginRight: 2 }}>{p.emoji}</span> {p.label}
                </button>
              );
            })}
          </div>

          <div className="ads-filter-row" style={{ marginBottom: 10 }} data-testid="ads-filter-formats">
            <button
              className={`ads-chip ${videoOnly ? "active" : ""}`}
              onClick={() => setVideoOnly((v) => !v)}
              data-testid="ads-filter-video-only"
              style={{ background: videoOnly ? "#7c3aed" : undefined, borderColor: videoOnly ? "#7c3aed" : undefined }}
            >
              <Film size={12} /> {t.studio_gallery_video_only || "Video only"}
            </button>
            <button className={`ads-chip ${activeFmt === "all" ? "active" : ""}`} onClick={() => setActiveFmt("all")}>{t.studio_gallery_all_formats || "All formats"}</button>
            {formats.map((f) => (
              <button key={f.id} className={`ads-chip ${activeFmt === f.id ? "active" : ""}`} onClick={() => setActiveFmt(f.id)}>
                {FORMAT_LABEL[f.id] || f.id}
              </button>
            ))}
          </div>
          <div className="ads-filter-row" style={{ marginBottom: 24 }} data-testid="ads-filter-categories">
            <button className={`ads-chip ${activeCat === "all" ? "active" : ""}`} onClick={() => setActiveCat("all")}>{t.studio_gallery_all_categories || "All categories"}</button>
            {categories.map((c) => (
              <button key={c} className={`ads-chip ${activeCat === c ? "active" : ""}`} onClick={() => setActiveCat(c)}>
                {CATEGORY_LABEL[c] || c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="ads-empty-state"><Loader2 className="animate-spin" /> {t.studio_gallery_loading_templates || "Loading templates…"}</div>
          ) : templates.length === 0 ? (
            <div className="ads-empty-state">{t.studio_gallery_no_match || "No templates match those filters."}</div>
          ) : (
            <>
              <div className="ads-template-grid" data-testid="ads-template-grid">
                {templates.map((tpl) => (
                <div
                  key={tpl.template_id}
                  className="ads-template-card"
                  data-animated={tpl.animated ? "true" : "false"}
                  onClick={() => setPreviewTpl(tpl)}
                  data-testid={`ads-template-${tpl.template_id}`}
                >
                  <div style={{ position: "relative" }}>
                    <TemplatePreview canvas={tpl.canvas_json} w={tpl.width} h={tpl.height} />
                    {tpl.animated && (
                      <span style={{
                        position: "absolute", top: 8, left: 8,
                        background: "linear-gradient(90deg,#a855f7,#7c3aed)",
                        color: "#fff", padding: "3px 8px", borderRadius: 999,
                        fontSize: 10, fontWeight: 800, letterSpacing: 0.6,
                        display: "flex", alignItems: "center", gap: 3,
                      }} data-testid="ads-template-animated">
                        <Film size={10} /> VIDEO
                      </span>
                    )}
                  </div>
                  <div className="ads-template-meta">
                    <div>
                      <div className="ads-template-title">{tpl.title}</div>
                      <div className="ads-template-tag">{FORMAT_LABEL[tpl.format] || tpl.format}{tpl.animated ? ` · ${(tpl.duration_ms / 1000).toFixed(1)}s` : ""}</div>
                    </div>
                    {tpl.premium
                      ? <span className="ads-badge-premium">PRO</span>
                      : <span className="ads-badge-free">FREE</span>}
                  </div>
                </div>
              ))}
              </div>

              {totalPages > 1 && (
                <div className="ads-pager" data-testid="ads-gallery-pager">
                  <button
                    className="ads-btn ads-btn-ghost"
                    disabled={page === 0}
                    onClick={() => { setPage(p => Math.max(0, p - 1)); window.scrollTo({ top: 300, behavior: "smooth" }); }}
                    data-testid="ads-pager-prev"
                  >{t.studio_gallery_prev || "← Prev"}</button>
                  <div className="ads-pager-info" data-testid="ads-pager-info">
                    {(t.studio_gallery_page_of || "Page {n} of {total}").replace("{n}", page + 1).replace("{total}", totalPages)}
                  </div>
                  <button
                    className="ads-btn ads-btn-ghost"
                    disabled={page >= totalPages - 1}
                    onClick={() => { setPage(p => Math.min(totalPages - 1, p + 1)); window.scrollTo({ top: 300, behavior: "smooth" }); }}
                    data-testid="ads-pager-next"
                  >{t.studio_gallery_next || "Next →"}</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <AchievementCelebration />
    </div>
    {previewTpl && (
      <TemplatePreviewModal
        template={previewTpl}
        onClose={() => setPreviewTpl(null)}
        onUse={(tpl) => startFromTemplate(tpl)}
        starting={starting === previewTpl?.template_id ? previewTpl.template_id : null}
        t={t}
      />
    )}
    </StudioShell>
  );
}

/** Lightweight SVG preview of the template (no Fabric needed). */
function TemplatePreview({ canvas, w, h }) {
  if (!canvas) return <div className="ads-template-preview" />;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="ads-template-preview" preserveAspectRatio="xMidYMid meet">
      <rect x={0} y={0} width={w} height={h} fill={canvas.background || "#000"} />
      {(canvas.objects || []).map((o, i) => {
        if (o.type === "rect") {
          return (
            <rect key={i}
              x={o.left || 0} y={o.top || 0}
              width={o.width || 0} height={o.height || 0}
              fill={o.fill || "#000"} opacity={o.opacity ?? 1}
              rx={o.rx || 0} ry={o.ry || 0}
              transform={o.angle ? `rotate(${o.angle} ${(o.left || 0) + (o.width || 0) / 2} ${(o.top || 0) + (o.height || 0) / 2})` : undefined}
            />
          );
        }
        if (o.type === "textbox") {
          return (
            <text key={i}
              x={(o.left || 0) + (o.textAlign === "center" ? (o.width || 0) / 2 : 0)}
              y={(o.top || 0) + (o.fontSize || 16)}
              fontFamily={o.fontFamily || "Inter"}
              fontWeight={o.fontWeight || 400}
              fontSize={o.fontSize || 20}
              fill={o.fill || "#fff"}
              opacity={o.opacity ?? 1}
              textAnchor={o.textAlign === "center" ? "middle" : "start"}
            >
              {(o.text || "").slice(0, 42)}
            </text>
          );
        }
        if (o.type === "image" && o.src) {
          return (
            <image key={i}
              href={o.src} xlinkHref={o.src}
              x={o.left || 0} y={o.top || 0}
              width={o.width || 0} height={o.height || 0}
              preserveAspectRatio="xMidYMid slice"
              opacity={o.opacity ?? 1}
            />
          );
        }
        if (o.type === "circle") {
          const cx = (o.left || 0) + (o.radius || 0);
          const cy = (o.top || 0) + (o.radius || 0);
          return (
            <circle key={i} cx={cx} cy={cy} r={o.radius || 0}
              fill={o.fill || "#fff"} opacity={o.opacity ?? 1} />
          );
        }
        return null;
      })}
    </svg>
  );
}
